import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { Channel } from '../../shared/host';
import type { RefreshTokenFn } from '../../shared/api/fetchWithRefresh';
import type { OutlookSurface } from '../pills/outlookPills';
import type { ChatMessage } from '../types';
import { saveChatHistory, wasChatCleared } from '../chatHistoryStorage';
import { reconcileTranscript } from '../threadReconcile';
import {
  getThreadMessages,
  listThreads,
  mapThreadMessage,
  resolveMultipartCursors,
  ThreadNotFoundError,
} from '../services/threadsApi';

/**
 * Server-thread hydration for chat v2 (AIVA-5929).
 *
 * The server thread store is the source of truth for the transcript;
 * localStorage is only a render cache. On mount (or when the API token first
 * becomes available) this hook reconciles the cache against the server:
 *
 *  - cached threadId → fetch its messages and reconcile them against the
 *    rendered transcript (see ``reconcileTranscript``).
 *  - no cached threadId, nothing rendered → list this host+context's threads and
 *    adopt the most recently updated one; none → stay empty.
 *  - no cached threadId but something rendered → keep it and adopt nothing (a
 *    legacy v1 bucket keeps rendering, but its client-minted sessionId is NOT a
 *    server thread, so the next send creates a fresh thread).
 *
 * AIVA-6899 — hydration is **non-destructive**. It used to overwrite the render
 * with the raw server snapshot, which silently discarded every action pill and
 * email/search card (the server never stores those — see ``threadReconcile``),
 * and blanked the pane outright when the thread had expired or came back empty.
 * Because Outlook remounts the task pane whenever the WebView is reclaimed
 * (working in another app for a while and coming back), that fired on a plain
 * app switch and looked like the conversation had been wiped. Now:
 *
 *  - the server snapshot is grafted onto the rendered transcript rather than
 *    replacing it, so pills and cards survive;
 *  - an empty snapshot never blanks a non-empty render;
 *  - a 404 (unknown/expired thread) keeps the transcript on screen and only
 *    nulls the thread id, so the next send starts a fresh server thread. The
 *    user's history is theirs until they clear it — see the ticket's expected
 *    result — even though the backend can no longer contribute that context.
 *
 * Race guard: results are dropped when a send or a chat clear started after
 * hydration began (each bumps the epoch), so a fresh turn is never clobbered
 * by a stale server snapshot.
 */

export interface UseThreadHydrationArgs {
  apiToken: string | null;
  host: Channel | null;
  /**
   * Outlook read/compose, read live at the moment hydration runs (AIVA-7622).
   * Passed straight through to `buildOutlookPills` so a rehydrated action row
   * matches the surface actually open — never persisted, and never should be:
   * "compose always wins" has to be evaluated fresh, not replayed from
   * whichever surface the original turn happened to run in. Null/undefined on
   * Word and PowerPoint, where it is simply unused.
   */
  outlookSurface?: OutlookSurface | null;
  contextId: string | null;
  /** Thread id restored from the localStorage cache (null for legacy v1). */
  initialThreadId: string | null;
  refresh?: RefreshTokenFn;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  /** useChat's message-id counter — persisted alongside the transcript. */
  msgCounterRef: MutableRefObject<number>;
  /**
   * Live mirror of the rendered transcript (useChat's ``messagesRef``). Read at
   * the moment a server snapshot lands so its client-only presentation state —
   * action pills, email cards, attachment badges — can be grafted back on
   * instead of being thrown away (AIVA-6899).
   */
  renderedRef: MutableRefObject<ChatMessage[]>;
}

export interface UseThreadHydrationReturn {
  /** The live server thread id; null until the backend mints one. */
  threadIdRef: MutableRefObject<string | null>;
  /** True once the initial server reconciliation finished (or was skipped). */
  hydrated: boolean;
  /** Null the thread so the next send creates a fresh server thread. */
  resetThread: () => void;
  /** Invalidate any in-flight hydration (called when a send starts). */
  notePendingSend: () => void;
}

export function useThreadHydration(args: UseThreadHydrationArgs): UseThreadHydrationReturn {
  const { apiToken, host, outlookSurface, contextId, refresh, setMessages, msgCounterRef, renderedRef } =
    args;
  const threadIdRef = useRef<string | null>(args.initialThreadId);
  const epochRef = useRef(0);
  const startedRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  // AIVA-7622: the hydration effect below runs exactly ONCE, gated only on
  // apiToken becoming available — but Office.js's mailbox-item binding (what
  // resolves `surface` from null to "read"/"compose") is its own async
  // sequence with no ordering guarantee against auth. A plain prop value
  // closed over inside that one-shot effect would freeze at whatever
  // `outlookSurface` happened to be at that single instant — null on a
  // genuinely fresh window if Office.js hadn't finished yet, silently
  // emptying buildOutlookPills's row for the rest of the pane's life. Mirror
  // it into a ref instead, exactly like `renderedRef` above, so the read
  // inside `applySnapshot` (which only happens after the async
  // getThreadMessages call resolves) sees whatever is current at that later
  // moment, not what was current at mount.
  const outlookSurfaceRef = useRef(outlookSurface);
  outlookSurfaceRef.current = outlookSurface;

  const resetThread = useCallback(() => {
    // Bumping the epoch also drops any in-flight hydration, so a slow fetch
    // can't resurrect the thread the caller just discarded.
    epochRef.current += 1;
    threadIdRef.current = null;
  }, []);

  const notePendingSend = useCallback(() => {
    epochRef.current += 1;
  }, []);

  useEffect(() => {
    // Idempotent: only the first available token triggers hydration. Without
    // a token we cannot call the threads API, so wait for sign-in.
    if (startedRef.current || !apiToken) return;
    startedRef.current = true;
    const epoch = epochRef.current;

    /**
     * Apply a server snapshot to the render, or null when it was declined.
     *
     * An empty snapshot against a non-empty render is always declined: the
     * message documents carry their own per-item TTL while the thread document's
     * slides forward on every write, so a live thread can legitimately answer
     * with zero messages — and blanking a transcript the client still holds is
     * never the better outcome.
     */
    const applySnapshot = (dtos: Awaited<ReturnType<typeof getThreadMessages>>): ChatMessage[] | null => {
      // Server ids are "srv-" prefixed by mapThreadMessage, so they can never
      // collide with the client's msg-{n} counter ids.
      const snapshot = resolveMultipartCursors(
        dtos.map((dto) => mapThreadMessage(dto, { host, surface: outlookSurfaceRef.current ?? null }))
      );
      const rendered = renderedRef.current;
      if (snapshot.length === 0 && rendered.length > 0) return null;
      const reconciled = reconcileTranscript(rendered, snapshot);
      setMessages(reconciled);
      return reconciled;
    };

    const hydrateFromThread = async (threadId: string) => {
      try {
        const dtos = await getThreadMessages(apiToken, threadId, refresh);
        if (epochRef.current !== epoch) return;
        applySnapshot(dtos);
      } catch (err) {
        if (epochRef.current !== epoch) return;
        if (err instanceof ThreadNotFoundError) {
          // Unknown/expired thread (3-day TTL). The transcript stays on screen
          // (AIVA-6899) — only the dead id is dropped, so the next send mints a
          // fresh server thread instead of retrying this one forever. Rewrite
          // the cache with the null id, since not touching `messages` means
          // useChat's save effect won't re-fire to do it for us.
          threadIdRef.current = null;
          saveChatHistory(
            host,
            { messages: renderedRef.current, threadId: null, msgCounter: msgCounterRef.current },
            contextId
          );
        }
        // Any other failure (network, 5xx): keep the cached render —
        // hydration is best-effort and must never blank a working chat.
      }
    };

    const adoptLatestServerThread = async () => {
      if (host === null) return;
      // Only ever adopt onto an EMPTY pane (a fresh device, cleared storage, or
      // a legacy v1 bucket with nothing cached). Outlook keys every thread into
      // one host-level bucket, so adopting "the most recent thread" over a
      // transcript the user can still see would swap their conversation for an
      // unrelated one — including right after a 404 left the render in place
      // with a null thread id (AIVA-6899). Declining leaves the id null, so the
      // next send mints a fresh server thread.
      if (renderedRef.current.length > 0) return;
      if (wasChatCleared(host, contextId)) return;
      try {
        const threads = await listThreads(apiToken, host, contextId, refresh);
        if (epochRef.current !== epoch || threads.length === 0) return;
        const [first, ...rest] = threads;
        // Narrow `first` for TS strict (noUncheckedIndexedAccess). The
        // length guard above already rules this out at runtime.
        if (first === undefined) return;
        const latest = rest.reduce(
          (a, b) => (Date.parse(b.updated_at) >= Date.parse(a.updated_at) ? b : a),
          first
        );
        // A document-scoped context (Word/PowerPoint) can hold several server
        // threads for the SAME document: a Word selection turn starts a fresh
        // thread so the LLM's checkpoint can't bleed across selections (see
        // resolveThreadReset), but that must not hide the earlier turns on
        // reopen. Merge every thread sharing this context, oldest first, into
        // one transcript. Outlook's context is null — one host-wide bucket
        // spanning unrelated emails, where separate threads really are
        // separate conversations — so only the latest is adopted there,
        // unchanged.
        const threadsToLoad =
          contextId !== null
            ? [...threads].sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at))
            : [latest];
        const dtoLists = await Promise.all(
          threadsToLoad.map(async (t) => {
            try {
              return await getThreadMessages(apiToken, t.thread_id, refresh);
            } catch (err) {
              // A single expired fragment (3-day TTL) must not blank the rest
              // of the merged transcript — just contribute nothing for it.
              if (err instanceof ThreadNotFoundError) return [];
              throw err;
            }
          })
        );
        if (epochRef.current !== epoch) return;
        const messages = applySnapshot(dtoLists.flat());
        if (messages === null) return;
        threadIdRef.current = latest.thread_id;
        saveChatHistory(
          host,
          { messages, threadId: latest.thread_id, msgCounter: msgCounterRef.current },
          contextId
        );
      } catch {
        // Listing failed (network, 5xx) — stay with the cached render.
      }
    };

    void (async () => {
      const cachedThreadId = threadIdRef.current;
      if (cachedThreadId) {
        await hydrateFromThread(cachedThreadId);
      } else {
        await adoptLatestServerThread();
      }
      setHydrated(true);
    })();
  }, [apiToken, host, outlookSurface, contextId, refresh, setMessages, msgCounterRef, renderedRef]);

  return { threadIdRef, hydrated, resetThread, notePendingSend };
}
